const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/appointment/components/AppointmentBookingPatientDataFields.tsx';
import textMap from 'App/pages/appointment/translations';
import { TextField, Typography } from '@mui/material';
import { AriaLabelType, Label } from 'App/components/old_components/components/Label';
import React from 'react';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';
import { css, cx } from '@emotion/css';
import { colors } from '@cistec/common_web/defs/colors';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { KeyValuePair } from '@cistec/common_web/defs/generalDefs';
import { PatientFormData } from '../BookAppointmentPage';
import { MaskedTextField } from '@cistec/common_web/components/MaskedTextField/MaskedTextField';
import { MetaKatItemBasicSelect } from '@cistec/common_web/components/Forms/components/MetaKat/MetaKatItemBasicSelect';
import { useSelector } from 'react-redux';
import { selectCountries } from 'App/store/metaKat';

interface Props {
    defaultValues: KeyValuePair;
    onChange: (fieldName: keyof PatientFormData, value: string) => void;
    errors: { [key: string]: string };
}

const cssLabelAndInputFieldWrapper = css`
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    width: 100%;

    & span > div {
        width: 100%;
    }

    & .MuiSelect-select {
        padding-left: 8px;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;
const cssInputFieldSmDeviceWrapper = css`
    display: flex;
    flex-direction: column;
    margin-top: 8px;

    & > div > div > button {
        top: -22px;
    }

    & .MuiSelect-select {
        padding-left: 8px;
    }
`;
const cssInputLabelWrapper = css`
    & div {
        margin: 0;
    }

    & div > button {
        margin-top: -14px;
    }
`;
const cssInputLabelStyle = css`
    width: 25%;
    text-align: left;
    color: ${colors.grey['600']};
`;

const cssInputWidthStyle = css`
    width: 75%;

    & > div {
        height: 39px;
    }
`;

const cssInputWidthSmDevice = css`
    width: 100%;
    color: ${colors.grey['600']};

    & > div {
        width: 100%;
        height: 39px;
    }
`;

export const AppointmentBookingPatientDataFields = (props: Props) => {
    const { onChange, errors, defaultValues } = props;
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isSmallDevice = useBreakpoint(theme?.breakpoints?.values?.sm);

    const renderTextField = (label: string, fieldName: keyof PatientFormData) => {
        return (
            <div className={isSmallDevice ? cssInputFieldSmDeviceWrapper : cssLabelAndInputFieldWrapper}>
                <Typography variant="body1" className={isSmallDevice ? cssInputWidthSmDevice : cssInputLabelStyle}>
                    {label} *
                </Typography>
                <TextField
                    variant="outlined"
                    defaultValue={defaultValues[fieldName]}
                    onChange={(e) => onChange(fieldName, e.target.value)}
                    className={isSmallDevice ? cssInputWidthSmDevice : cssInputWidthStyle}
                    error={!!errors[fieldName]}
                    helperText={errors[fieldName] || ''}
                    required
                />
            </div>
        );
    };
    const countries = useSelector(selectCountries);

    return (
        <div>
            {renderTextField(formatMessage(textMap.email), 'email')}
            {renderTextField(formatMessage(textMap.streetAndNumber), 'streetAndNr')}
            {renderTextField(formatMessage(textMap.postcode), 'postalcode')}
            {renderTextField(formatMessage(textMap.location), 'location')}
            <div className={isSmallDevice ? cssInputFieldSmDeviceWrapper : cssLabelAndInputFieldWrapper}>
                <Typography variant="body1" className={isSmallDevice ? cssInputWidthSmDevice : cssInputLabelStyle}>
                    {formatMessage(textMap.country)} *
                </Typography>
                <MetaKatItemBasicSelect
                    className={cx('CIS4mMetaKatDropDown', isSmallDevice ? cssInputWidthSmDevice : cssInputWidthStyle)}
                    favorites={['CH', 'DE', 'AT', 'LI', 'FR', 'IT']}
                    // Do not need to pass filter because filtering is done in this component
                    // filter={props.filter}
                    required={true}
                    labelPath={'Country'}
                    options={countries}
                />
            </div>
            <div className={isSmallDevice ? cssInputFieldSmDeviceWrapper : cssLabelAndInputFieldWrapper}>
                <Typography variant="body1" className={isSmallDevice ? cssInputWidthSmDevice : cssInputLabelStyle}>
                    {formatMessage(textMap.socialInsurance)} *
                </Typography>
                <div className={cssInputLabelWrapper}>
                    <Label
                        text={''}
                        aria={AriaLabelType.useText}
                        icon={'help'}
                        compTitle={textMap.ahvHelpTextTitle}
                        onClick={(e) => e.stopPropagation()}
                        isFormView={false}
                        marginTopInFormEdit={0}
                        helpText={formatMessage(textMap.ahvHelpText)}
                    />
                </div>
                <MaskedTextField
                    mask={'756.9999.9999.99'}
                    value={defaultValues['socialNr'] || ''}
                    onChange={(e) => onChange('socialNr', e.target.value)}
                    error={!!errors['socialNr']}
                    helperText={errors['socialNr'] || ''}
                    className={isSmallDevice ? cssInputWidthSmDevice : cssInputWidthStyle}
                    required
                />
            </div>
        </div>
    );
};
